import * as React from 'react'
import cx from 'classnames'
import styles from './SearchResults.sass'
import { CustomSelectProps } from '..'
import { GetItemPropsOptions } from 'downshift'
import { useObservableStream } from 'hooks/rx-hooks'
import { scrollActivityStream } from 'utils/dom-util'
import { combineLatest, interval } from 'rxjs'
import { filter, startWith, map, tap } from 'rxjs/operators'

interface Parent<
  V, // Value type
  M extends true | false, // Whether multi is on or not.
  VT = M extends false ? V : V[], // The value type, conditional based on multi>
> extends React.HTMLAttributes<HTMLDivElement>,
    Required<
      Pick<
        CustomSelectProps<V, M, VT>,
        | 'areItemsEqual'
        | 'multi'
        | 'renderSearchItem'
        | 'value'
        | 'itemToString'
      >
    > {}

interface Props<
  V, // Value type
  M extends true | false, // Whether multi is on or not.
  VT = M extends false ? V : V[], // The value type, conditional based on multi
> extends Parent<V, M, VT> {
  searchResults: V[]
  filteredSearchResults: V[]
  highlightedIndex: number | null
  isLoading: boolean
  getItemProps: (opts: GetItemPropsOptions<V>) => any
  onLoadMore: () => unknown
  isEoS: boolean
  placement: 'above' | 'below'
}

export default function SearchResults<
  V, // Value type
  M extends true | false, // Whether multi is on or not.
  VT = M extends false ? V : V[], // The value type, conditional based on multi
>({
  className,
  searchResults,
  value,
  highlightedIndex,
  filteredSearchResults,
  multi,
  areItemsEqual,
  renderSearchItem,
  getItemProps,
  itemToString,
  isEoS,
  isLoading,
  onLoadMore,
  placement,
  ...rest
}: Props<V, M, VT>) {
  const searchResultsScrollRef = React.useRef<HTMLDivElement | null>(null)
  useObservableStream(
    false,
    () =>
      // On scroll activity (+ on load), if we can't scroll
      // or if we reached a certain scroll thresh, trigger a load-more.
      combineLatest([
        scrollActivityStream(searchResultsScrollRef.current!, true).pipe(
          map(
            // If not scrollable, that means we want to load more.
            // Otherwise, if there's less than 100px to the bottom,
            // load more.
            (e) => !e.isScrollableY || e.scrollHeight - e.scrollBottom < 100,
          ),
        ),
        // Every so often, we re-emit the latest "should we load more".
        // The idea is that the scroll activity will emit a new value whenever
        // the scroll changes, which it will whenever new items come in.
        // If it doesn't change, it's possible that the load didn't return any new items,
        // but if it hasn't reported end-of-stream (EoS) yet (see below) we want to
        // emit again.
        interval(100).pipe(startWith(0)),
      ]).pipe(
        map(([v]) => v),
        filter((v) => v && !isEoS && !isLoading),
        tap(() => onLoadMore()),
      ),
    [onLoadMore, isEoS, isLoading],
  )

  return (
    <div
      ref={searchResultsScrollRef}
      className={cx(
        styles.root,
        placement === 'above' && styles.above,
        placement === 'below' && styles.below,
        className,
      )}
      data-test="search-result"
      {...rest}
    >
      {filteredSearchResults.length === 0 && isEoS && (
        <div data-test={`search-result-empty`} className={styles.empty}>
          No results.
        </div>
      )}
      {filteredSearchResults.map((item, i) => {
        const selected = multi ? false : areItemsEqual(item, value as any)
        const highlighted = i === highlightedIndex

        return (
          <React.Fragment key={i}>
            {renderSearchItem({
              item,
              selected,
              highlighted,
              itemToString,
              'data-test': 'search-result-item',
              ...getItemProps({ item, index: i }),
            })}
          </React.Fragment>
        )
      })}
    </div>
  )
}
